/**
 * Layout for a "page" within our marketing sites experience
 *
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { graphql, useStaticQuery } from 'gatsby'
import { Context as ResponsiveContext } from 'react-responsive'
import SEO from 'components/SEO'
import NavItems from 'components/contentful/ContentfulNavItems'
import GlobalFooter from 'components/contentful/GlobalFooter'
import SectionBlocks from 'components/contentful/SectionBlocks'
import 'styles/global.scss'

const Layout = ({
  location,
  title,
  slug,
  rootPath,
  isHomepage,
  metaTitle,
  metaDescription,
  navItems,
  sectionBlocks,
  noindex,
  disableFooter,
  children,
}) => {
  // To support gatsby SSR, we have to set up a window resize listener to automatcally
  // update the window width to apply the appropriate react-responsive media settings.
  // See: https://github.com/contra/react-responsive#supplying-through-context
  const [width, setWidth] = useState(600);
  const handleWindowResize = () => {
    setWidth(window?.innerWidth);
  }

  useEffect(() => {
    if (window) {
      window.addEventListener('resize', handleWindowResize);
      handleWindowResize()
    }

    return () => window?.removeEventListener('resize', handleWindowResize);
  }, [])

  const footerData = useStaticQuery(graphql`
    query {
      contentfulGlobalFooter(slug: { eq: "aboutFooter" }) {
        ...GlobalFooterFragment
      }
    }
  `)

  const globalFooter = footerData?.contentfulGlobalFooter || {}
  const meta = [
    {
      property: 'robots',
      content: noindex ? 'noindex' : 'index',
    },
  ]

  return (
    <ResponsiveContext.Provider value={{ width }}>
      <SEO
        title={metaTitle || title}
        description={metaDescription?.metaDescription}
        meta={meta}
        slug={(slug && !isHomepage) ? `${rootPath}/${slug}` : `/`} />
      <NavItems location={location} {...navItems} />
      <div className="content-wrapper">
        {!!sectionBlocks && (
          <SectionBlocks sectionBlocks={sectionBlocks} />
        )}
        {children}
      </div>
      {!disableFooter ? (
        <GlobalFooter {...globalFooter} />
      ) : null}
    </ResponsiveContext.Provider>
  )
}

Layout.propTypes = {
  location: PropTypes.object,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  isHomepage: PropTypes.bool,
  rootPath: PropTypes.string,
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.object,
  navItems: PropTypes.object,
  sectionBlocks: PropTypes.array,
  noindex: PropTypes.bool,
  disableFooter: PropTypes.bool,
  children: PropTypes.node,
}

Layout.defaultProps = {
  rootPath: '',
  isHomepage: false,
  sectionBlocks: [],
}

export default Layout
